import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchESignatureCredentialStatus,
  type ESignatureCredentialStatus,
} from './esignatureCredentialApi';

// Keyed by patient and task, because default_signature_date differs per entry:
// it is the task's own date, or today in that patient's clinic zone. The
// credential half is user-scoped and identical everywhere. One shared entry
// would seed a dialog from whichever task or clinic was fetched first.
const cache = new Map<string, ESignatureCredentialStatus>();
const inFlight = new Map<string, Promise<ESignatureCredentialStatus>>();
// Bumped on every invalidation. A request in flight when the cache is dropped
// cannot be cancelled, so it carries the generation it was issued under and its
// result is discarded if that no longer matches — otherwise a response fetched
// before the credential changed could land afterwards and restore the old answer.
let generation = 0;

/** Cache key for a fetch; empty parts mean "no patient / task in scope". */
const keyFor = (patientId?: string, taskId?: string): string =>
  `${patientId ?? ''}|${taskId ?? ''}`;

/** Drop the cache after any change, so the next dialog reads fresh state. */
export function invalidateESignatureCredentialStatus(): void {
  cache.clear();
  inFlight.clear();
  generation += 1;
}

export interface UseESignatureCredentialResult {
  status: ESignatureCredentialStatus | null;
  isLoading: boolean;
  error: Error | null;
  /** Refetch, showing the loading state. */
  refresh: () => Promise<void>;
  /** Refetch in the background, keeping the current status on screen. */
  revalidate: () => Promise<void>;
  /** True when the last background recheck failed, so `status` may be stale. */
  isStale: boolean;
}

/**
 * Loads whether the signed-in user has an e-signature password.
 *
 * The signing dialog needs this to choose between asking for the existing
 * password and walking a first-time signer through creating one. Cached per page
 * session like `useActiveAttestations`, because every sign dialog on the page
 * asks the same question and the answer only changes when this user changes it.
 *
 * Pass `enabled: false` when the caller does not take a password at all (a QA
 * approval). Hooks cannot be called conditionally, so the flag is what keeps a
 * dialog with no password field from fetching credential status it will never
 * read, and from surfacing a lockout that does not apply to it.
 */
export function useESignatureCredential(
  enabled = true,
  patientId?: string,
  taskId?: string
): UseESignatureCredentialResult {
  const key = keyFor(patientId, taskId);
  const [status, setStatus] = useState<ESignatureCredentialStatus | null>(
    cache.get(key) ?? null
  );
  const [isLoading, setIsLoading] = useState(enabled && !cache.has(key));
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);
  const statusRef = useRef(status);
  statusRef.current = status;

  const load = useCallback(
    async ({ keepStatusOnError = false } = {}): Promise<void> => {
      const issuedAt = generation;
      try {
        const p =
          inFlight.get(key) ??
          fetchESignatureCredentialStatus(patientId, taskId);
        inFlight.set(key, p);
        const data = await p;
        if (issuedAt !== generation) {
          // The cache was invalidated while this was in flight, so this answer
          // predates whatever changed. Drop it and let the newer load win.
          return;
        }
        cache.set(key, data);
        inFlight.delete(key);
        setStatus(data);
        setError(null);
        setIsStale(false);
      } catch (e) {
        if (issuedAt !== generation) {
          return;
        }
        inFlight.delete(key);
        console.error('Failed to load e-signature credential status', e);
        if (keepStatusOnError && statusRef.current !== null) {
          // A background recheck failed. Keep the status already on screen —
          // replacing a usable password form with an error screen over a
          // transient blip would stop a clinician signing for no reason — but
          // mark it stale so callers do not submit against a status that may
          // predate a change made elsewhere.
          setIsStale(true);
          return;
        }
        setError(
          new Error(
            'Could not check your e-signature setup. Refresh the page or try again shortly.'
          )
        );
      } finally {
        setIsLoading(false);
      }
    },
    [key, patientId, taskId]
  );

  const refresh = useCallback(async (): Promise<void> => {
    invalidateESignatureCredentialStatus();
    setIsLoading(true);
    await load();
  }, [load]);

  const revalidate = useCallback(async (): Promise<void> => {
    // No loading state, and a failure leaves the current status in place: the
    // caller already has something usable on screen and only wants it corrected
    // if it has since changed. Used when a signing dialog reopens, because
    // creating the credential during a signature happens on the server and would
    // otherwise leave this cache claiming the user still has none.
    invalidateESignatureCredentialStatus();
    await load({ keepStatusOnError: true });
  }, [load]);

  useEffect(() => {
    let cancelled = false;

    if (!enabled) {
      setIsLoading(false);
      return;
    }

    const hit = cache.get(key);
    if (hit) {
      setStatus(hit);
      setIsLoading(false);
      return;
    }

    void (async () => {
      await load();
      if (cancelled) {
        return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [load, enabled, key]);

  return { status, isLoading, error, refresh, revalidate, isStale };
}
